
import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Users, Shield } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SocialAuth from "@/components/auth/SocialAuth";
import LoginForm from "@/components/auth/LoginForm";
import { useAuth } from "@/contexts/AuthContext";
import { useReferralUrlParams } from "@/hooks/referrals/useReferralUrlParams";
import { useOAuthSignUpProcess } from "@/hooks/useOAuthSignUpProcess";

const Auth = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [initComplete, setInitComplete] = useState(false);

  // Process URL parameters for referrals and affiliates
  useReferralUrlParams();
  
  // Process OAuth signup completions
  useOAuthSignUpProcess();

  // Effect for redirecting authenticated users
  useEffect(() => {
    if (!isLoading) {
      setInitComplete(true);
      
      if (isAuthenticated) {
        // Check for a stored redirect URL
        const redirectUrl = localStorage.getItem('redirectUrl');
        if (redirectUrl) {
          localStorage.removeItem('redirectUrl');
          navigate(redirectUrl);
        } else {
          navigate('/marketplace');
        }
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading || !initComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-hero-dark/80 to-hero-mid/80">
        <div className="animate-pulse text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'url("/lovable-uploads/7f72d2f9-18ce-48a5-8cb7-7c34071c5400.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div className="relative z-10 min-h-screen bg-gradient-to-b from-hero-dark/80 to-hero-mid/80">
        <Header />
        <div className="container mx-auto px-4 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Card className="w-full max-w-md mx-auto relative z-10 mt-8 md:mt-0 bg-white/95 backdrop-blur-md shadow-xl">
            <CardHeader className="text-center space-y-4 px-6">
              <CardTitle className="text-2xl md:text-3xl font-bold text-[#122D44]">
                Welcome Back!
              </CardTitle>
              <CardDescription className="text-base md:text-lg text-gray-700 font-medium">
                Log in to access your expert-verified strategies
              </CardDescription>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>10,000+ active traders</span>
                <Shield className="w-4 h-4 ml-3" />
                <span>Expert-backed strategies</span>
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-8">
              <div className="space-y-6">
                <SocialAuth />
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or continue with email</span>
                  </div>
                </div>
                <LoginForm />
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="relative z-20">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Auth;
